import { OVERLAY_SLOTS, buildDefaultOverlays, A5_PORTRAIT_MM } from "./GiftCardOverlayEditor";

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const clamp = (v, min, max) => Math.min(max, Math.max(min, Number(v) || 0));

/**
 * Open a print-ready A5 landscape sheet in a new tab with the gift
 * card layout rendered at the physical size (210 × 148 mm). Uses
 * `@page size: A5 landscape;` so the browser's print dialog defaults
 * to the correct paper. Coordinates + sizes are the same 0-100 %
 * values as the on-screen preview, so what the operator designs is
 * exactly what prints.
 *
 * `sample` lets the caller inject real values (recipient name, sold
 * amount, expiry, barcode) — falls back to placeholders when the
 * button is triggered from the brand editor.
 */
export function openGiftCardPrint(template, sample = {}) {
  const overlays = template.overlays && Object.keys(template.overlays).length
    ? { ...buildDefaultOverlays(), ...template.overlays }
    : buildDefaultOverlays();

  const values = {
    to_name: sample.to_name || "A Gift For: Guest name",
    from_name: sample.from_name || "From: Buyer name",
    value: sample.value || "IDR 1,000,000",
    expiry: sample.expiry || "Valid until MM / YYYY",
    barcode: sample.barcode || "GC-XXXX-XXXX",
  };

  const overlayHtml = OVERLAY_SLOTS.map((s) => {
    const o = { ...s.defaults, ...(overlays[s.key] || {}) };
    if (!o.visible) return "";
    const left = clamp(o.x, 0, 100);
    const top = clamp(o.y, 0, 100);
    const zIndex = s.key === "main_image" ? 0 : 1;
    if (s.kind === "zone") {
      const w = clamp(o.w ?? s.defaults.w, 10, 100);
      const h = clamp(o.h ?? s.defaults.h, 4, 80);
      const align = o.align || s.defaults.align || "left";
      // Same unified size formula as single-line text slots so the
      // slider number is a real physical constant across every text
      // overlay. Old rows that only carry `font_size` are back-
      // scaled 5× so they print at the historical visual size.
      const rawSize = o.size ?? (o.font_size != null ? o.font_size * 5 : s.defaults.size);
      const size = clamp(rawSize, 4, 100);
      const fontMm = (size / 100) * A5_PORTRAIT_MM.width * 0.4;
      const text = s.sourceField === "__value__"
        ? String(values.value || "")
        : String(template[s.sourceField] || "");
      return `
        <div class="slot slot-zone" style="left:${left}%;top:${top}%;width:${w}%;height:${h}%;text-align:${align};color:${o.color || "#2C2D28"};font-size:${fontMm.toFixed(2)}mm;justify-content:${align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start"};z-index:${zIndex}">
          <span style="display:inline-block;width:100%;text-align:${align}">${escapeHtml(text)}</span>
        </div>`;
    }
    if (s.key === "main_image") {
      if (!template.background_image_data_url) return "";
      const w = clamp(o.size, 20, 200);
      return `<img class="slot slot-main" style="left:${left}%;top:${top}%;width:${w}%;z-index:${zIndex}" src="${template.background_image_data_url}" alt="" />`;
    }
    if (s.key === "logo") {
      if (!template.logo_image_data_url) return "";
      return `<img class="slot slot-logo" style="left:${left}%;top:${top}%;width:${clamp(o.size, 4, 100)}%;z-index:${zIndex}" src="${template.logo_image_data_url}" alt="" />`;
    }
    if (s.key === "barcode") {
      const w = clamp(o.size, 4, 100);
      return `
        <div class="slot slot-barcode" style="left:${left}%;top:${top}%;width:${w}%;z-index:${zIndex}">
          <div class="bars"></div>
          <span>${escapeHtml(values.barcode)}</span>
        </div>`;
    }
    // Text overlays — size becomes a mm-based font-size relative to
    // the A5 portrait width (148mm) so the print output matches the
    // on-screen preview.
    // Text overlays — size becomes a mm-based font-size relative to
    // the A5 portrait width (148mm) so the print output matches the
    // on-screen preview. Anchored at the LEFT edge so overlays sharing
    // the same X value sit in a perfectly straight vertical column.
    const size = clamp(o.size, 4, 100);
    const fontMm = (size / 100) * A5_PORTRAIT_MM.width * 0.4;
    const weight = s.key === "value" ? 700 : 500;
    const letter = s.key === "value" ? "-0.01em" : "normal";
    const align = (o.align || s.defaults.align || "left").toLowerCase();
    const translateX = align === "center" ? "-50%" : align === "right" ? "-100%" : "0";
    return `
      <div class="slot slot-text" style="left:${left}%;top:${top}%;transform:translate(${translateX},-50%);text-align:${align};padding:0;margin:0;color:${o.color || "#2C2D28"};font-size:${fontMm.toFixed(2)}mm;font-weight:${weight};letter-spacing:${letter};z-index:${zIndex}">
        ${escapeHtml(values[s.key] || "")}
      </div>`;
  }).join("\n");

  const bgColor = template.background_color || "#F9F8F6";

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(template.title || "Gift Card")}</title>
    <style>
      @page { size: A5; margin: 0; }
      html, body { margin: 0; padding: 0; background: #f5f2ec; }
      .card {
        position: relative;
        width: ${A5_PORTRAIT_MM.width}mm;
        height: ${A5_PORTRAIT_MM.height}mm;
        margin: 0 auto;
        background: ${bgColor};
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        color: #2C2D28;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }
      .bg {
        position: absolute; inset: 0; width: 100%; height: 100%;
        object-fit: cover; pointer-events: none; user-select: none;
      }
      .slot {
        position: absolute; transform: translate(-50%, -50%);
        text-align: center; max-width: 92%;
      }
      .slot-text { white-space: nowrap; line-height: 1.15; }
      .slot-zone {
        display: flex; align-items: flex-start; line-height: 1.3;
        overflow: hidden; padding: 0.5mm 1mm; white-space: pre-wrap;
        word-break: break-word; max-width: none;
      }
      .slot-logo { object-fit: contain; }
      .slot-main {
        object-fit: contain; pointer-events: none; user-select: none;
        max-width: none; height: auto;
      }
      .slot-barcode { display: flex; flex-direction: column; align-items: center; }
      .slot-barcode .bars {
        width: 100%; height: 8mm; border-radius: 0.5mm;
        background-image: repeating-linear-gradient(90deg, #111 0 0.6mm, transparent 0.6mm 1.2mm, #111 1.2mm 1.5mm, transparent 1.5mm 2.7mm, #111 2.7mm 3.3mm, transparent 3.3mm 3.9mm);
      }
      .slot-barcode span {
        margin-top: 1.5mm; font-family: "Courier New", monospace;
        font-size: 3mm; letter-spacing: 0.4mm;
      }
      @media print {
        html, body { background: #fff; }
        .card { box-shadow: none; }
      }
    </style>
  </head>
  <body>
    <div class="card">
      ${overlayHtml}
    </div>
    <script>
      // Give the images a beat to decode before firing the print dialog,
      // otherwise Safari sometimes prints a blank sheet.
      window.addEventListener("load", () => {
        setTimeout(() => window.print(), 300);
      });
    </script>
  </body>
</html>`;

  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}
